"use client";

import { useEffect, useRef, useState } from "react";

import { SegmentedToggle } from "@/components/segmentedToggle/segmentedToggle.component";
import { Text } from "@/components/text/text.component";

import { brown } from "@/app/font";

import { CompareRowLabel } from "../compareRowLabel/compareRowLabel.component";
import type { SanityCompareTableQueryResult } from "./compare.type";
import { CompareTableGroup } from "./compareTableGroup.component";
import { CompareTableMobile } from "./compareTableMobile.component";
import { useCompareTableSectionState } from "./useCompareTableSectionState";

export const CompareTableSection = ({
  block,
  pageType,
}: CompareTableSectionProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [showFade, setShowFade] = useState(true);

  const {
    filterOptions,
    filter,
    setFilter,
    setSelectedProduct,
    setMobileCompareRight,
    activeView,
    table,
  } = useCompareTableSectionState(block);

  if (!activeView || !table) return null;

  const {
    columns,
    rows,
    gridColumns,
    compareLeftId,
    compareRightId,
    compareLeftIndex,
    compareRightIndex,
  } = table;
  const footnotes = (activeView.footnotes ?? []).filter(Boolean);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const handleScroll = () => {
      setShowFade(el.scrollLeft <= 5);
    };

    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };

    const handleMouseUp = () => {
      isDown = false;
      el.style.cursor = "grab";
      el.style.removeProperty("user-select");
    };

    const handleMouseLeave = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = "grab";
      el.style.removeProperty("user-select");
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      el.scrollLeft = scrollLeft - (x - startX);
    };

    el.style.cursor = "grab";
    handleScroll();

    el.addEventListener("scroll", handleScroll);
    el.addEventListener("mousedown", handleMouseDown);
    el.addEventListener("mouseup", handleMouseUp);
    el.addEventListener("mouseleave", handleMouseLeave);
    el.addEventListener("mousemove", handleMouseMove);

    return () => {
      el.removeEventListener("scroll", handleScroll);
      el.removeEventListener("mousedown", handleMouseDown);
      el.removeEventListener("mouseup", handleMouseUp);
      el.removeEventListener("mouseleave", handleMouseLeave);
      el.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const renderCompareTableLabels = () => {
    if (!rows?.length) return null;

    return (
      <>
        {rows.map((row) => (
          <div
            role="rowheader"
            key={row.id}
            className="border-greyscale-strength h-[100px] border-b px-4 py-8">
            <CompareRowLabel
              label={row.label}
              info={row.info}
              variant="body5"
            />
          </div>
        ))}
      </>
    );
  };

  const renderCompareTableFootnotes = () => {
    if (!footnotes?.length) return null;

    return (
      <ul className="mt-3 space-y-1 px-4 text-xs text-slate-500">
        {footnotes.map((disclaimer, index) => (
          <li key={`disclaimer-${index}`}>{disclaimer}</li>
        ))}
      </ul>
    );
  };

  return (
    <section
      className="container my-10 w-full max-w-[1376px] bg-white px-0 md:my-16 md:px-9"
      id="compare">
      <div className="mx-5 mb-6 flex flex-col items-center justify-between gap-10 text-center md:mx-0 md:mb-6">
        <div className="flex flex-col items-center gap-2">
          <Text variant="h3" component="h2">
            {block.title}
          </Text>
          {block.description?.trim() ? (
            <Text
              variant="body1"
              className="text-greyscale-body max-w-2xl"
              data-testid="compare-table-description">
              {block.description}
            </Text>
          ) : null}
        </div>

        {filterOptions.length > 1 ? (
          <SegmentedToggle
            ariaLabel="Comparison tabs"
            options={filterOptions}
            value={filter}
            onChange={setFilter}
            className="max-w-xs"
            fontClassName={
              pageType === "shopPage" || pageType === "monitoringPage"
                ? brown.className
                : undefined
            }
          />
        ) : null}
      </div>

      <div
        className="block lg:hidden"
        data-testid="compare-mobile"
        role="table"
        aria-label="Compare ADT Blu equipment">
        <CompareTableMobile
          columns={columns}
          rows={rows}
          compareLeftId={compareLeftId}
          compareRightId={compareRightId}
          compareLeftIndex={compareLeftIndex}
          compareRightIndex={compareRightIndex}
          onSelectCompareLeft={setSelectedProduct}
          onSelectCompareRight={setMobileCompareRight}
        />
      </div>

      <div
        ref={scrollRef}
        role="table"
        aria-label="Compare ADT Blu equipment"
        data-testid="compare-list"
        className="scrollbar-hide hidden min-w-0 overflow-x-auto lg:grid"
        style={{ gridTemplateColumns: gridColumns }}>
        <div
          className="sticky left-0 z-10 w-[198px] items-start gap-2 bg-white"
          role="row">
          <div
            className="border-greyscale-strength h-[250px] border-b"
            aria-hidden="true"
          />
          {renderCompareTableLabels()}
        </div>
        <CompareTableGroup columns={columns} rows={rows} showFade={showFade} />
      </div>

      {renderCompareTableFootnotes()}
    </section>
  );
};

type CompareTableSectionProps = {
  block: SanityCompareTableQueryResult;
  pageType?: string;
};
